import React from 'react';

import { GrafanaTheme2 } from '@grafana/data';
import { CollapsableSection, Icon, Tooltip, useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';

import { Analysis, AnalysisStep } from 'types';
import { getAnalysisDisplayTitle } from 'utils/utils.sift';

interface CheckStepsProps {
  analysis: Analysis;
}

function CheckSteps({ analysis }: CheckStepsProps): React.ReactElement | null {
  const styles = useStyles2(getStyles);

  const steps: AnalysisStep[] = analysis.steps ?? [];

  if (steps.length < 1) {
    return null;
  }

  steps.sort((a, b) => (a.created > b.created ? 1 : -1));

  return (
    <div className={styles.stepList}>
      <h2 className={styles.stepHeader}>{getAnalysisDisplayTitle(analysis.name, analysis.title)} Log</h2>
      {steps.map((step) => {
        const displayTime = new Date(step.created).toLocaleString();
        const isError = (step.exitMessage ?? '') !== '';
        const metadata = step.metadata;
        const exitMeta = step.exitMessageMeta;
        const stats = step.stats;

        return (
          <div className={styles.stepLine} key={step.id}>
            <div className={isError ? styles.iconError : styles.iconSucess}>
              <Tooltip content={displayTime}>
                <Icon
                  title={isError ? step.exitMessage : step.state}
                  name={isError ? 'exclamation-triangle' : 'check'}
                />
              </Tooltip>
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepMessage}>
                {metadata?.title || step.state}
                {metadata?.description && (
                  <Tooltip content={metadata.description}>
                    <Icon name="info-circle" className={styles.infoIcon} />
                  </Tooltip>
                )}
              </div>
              {stats && Object.keys(stats).length > 0 && (
                <div className={styles.stepStats}>
                  {Object.entries(stats).map(([key, value]) => (
                    <span key={key} className={styles.statItem}>
                      {metadata?.statFormatting?.[key]?.template
                        ? metadata.statFormatting[key].template.replace('%v', value.toLocaleString())
                        : `${key}: ${value}`}
                    </span>
                  ))}
                </div>
              )}
              {isError && (
                <div className={styles.stepError}>
                  <CollapsableSection
                    label={<div className={styles.errorTitle}>{exitMeta?.title || step.exitMessage}</div>}
                    isOpen={false}
                  >
                    {exitMeta?.description && <div className={styles.errorDescription}>{exitMeta.description}</div>}
                    {exitMeta?.docsUrl && (
                      <a href={exitMeta.docsUrl} target="_blank" rel="noopener noreferrer" className={styles.docsLink}>
                        <Icon name="book" /> Learn more
                      </a>
                    )}
                  </CollapsableSection>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getStyles(theme: GrafanaTheme2) {
  return {
    stepList: css`
      padding: 20px;
      background-color: ${theme.colors.background.secondary};
    `,
    stepHeader: css`
      width: 100%;
      margin-bottom: ${theme.spacing(2)};
    `,
    stepLine: css`
      display: flex;
      padding: ${theme.spacing(1)};
      border-bottom: 1px solid ${theme.colors.border.weak};
      &:last-child {
        border-bottom: none;
      }
    `,
    stepContent: css`
      flex: 1;
      margin-left: ${theme.spacing(2)};
    `,
    iconSucess: css`
      width: 16px;
      stroke: ${theme.colors.success.main};
    `,
    iconError: css`
      width: 16px;
      stroke: ${theme.colors.error.main};
    `,
    stepMessage: css`
      font-weight: ${theme.typography.fontWeightMedium};
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
    `,
    stepStats: css`
      margin-top: ${theme.spacing(0.5)};
      color: ${theme.colors.text.secondary};
      font-size: ${theme.typography.bodySmall.fontSize};
    `,
    statItem: css`
      margin-right: ${theme.spacing(2)};
      &:last-child {
        margin-right: 0;
      }
    `,
    stepError: css`
      margin-top: ${theme.spacing(1)};
      padding: ${theme.spacing(0.5)} ${theme.spacing(1.5)};
      background: ${theme.colors.background.secondary};
      border: 1px solid ${theme.colors.border.weak};
      border-radius: ${theme.shape.radius.default};
    `,
    errorTitle: css`
      color: ${theme.colors.text.primary};
      font-size: ${theme.typography.bodySmall.fontSize};
      font-weight: ${theme.typography.fontWeightMedium};
      display: flex;
      align-items: center;
    `,
    errorDescription: css`
      margin-top: ${theme.spacing(0.25)};
      color: ${theme.colors.text.secondary};
      font-size: ${theme.typography.bodySmall.fontSize};
    `,
    infoIcon: css`
      color: ${theme.colors.text.secondary};
      cursor: help;
    `,
    docsLink: css`
      display: inline-flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
      margin-top: ${theme.spacing(0.5)};
      color: ${theme.colors.text.link};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    `,
  };
}

export { CheckSteps };
